import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ApiCacheService } from '../services/api-cache.service';

@Injectable()
export class CacheInterceptor implements HttpInterceptor {
  
  constructor(private cacheService: ApiCacheService) {}
  
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Only cache GET requests
    if (request.method !== 'GET') {
      return next.handle(request);
    }
    
    // Check if this request should be cached
    if (!this.shouldCacheRequest(request)) {
      return next.handle(request);
    }
    
    // Check for cache-control header
    const forceRefresh = request.headers.get('Cache-Control') === 'no-cache';
    const maxAge = this.getMaxAge(request);
    
    // Generate cache key from URL and params
    const cacheKey = this.createCacheKey(request);
    
    // Check if we have a cached response
    if (!forceRefresh) {
      const cachedResponse = this.cacheService.get(cacheKey, maxAge);
      if (cachedResponse) {
        return of(new HttpResponse({
          body: cachedResponse,
          status: 200,
          statusText: 'OK',
          url: request.url
        }));
      }
    }
    
    // If not cached or force refresh, make the request and cache the response
    return next.handle(request).pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          this.cacheService.set(cacheKey, event.body);
        }
      })
    );
  }
  
  /**
   * Determine if a request should be cached based on URL patterns
   */
  private shouldCacheRequest(request: HttpRequest<unknown>): boolean {
    // List of URL patterns to cache
    const cachablePatterns = [
      '/api/dashboard',
      '/api/articles',
      '/api/authors',
      '/api/campaigns',
      '/api/master',
    ];
    
    // Check if request has cache header
    const shouldCache = request.headers.get('X-Cache-Me');
    if (shouldCache === 'true') {
      return true;
    }
    
    // Check URL against patterns
    return cachablePatterns.some(pattern => request.url.includes(pattern));
  }
  
  /**
   * Create a unique cache key for a request
   */
  private createCacheKey(request: HttpRequest<unknown>): string {
    return `${request.url}|${request.params.toString()}`;
  }
  
  /**
   * Get max age from request headers or use default
   */
  private getMaxAge(request: HttpRequest<unknown>): number {
    const maxAgeHeader = request.headers.get('X-Cache-Max-Age');
    return maxAgeHeader ? parseInt(maxAgeHeader, 10) : 60 * 60 * 1000; // Default 1 hour
  }
} 